import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col, Button, Collapse } from 'react-bootstrap';

import { dateFormat, dateAdd, dateWeekDay } from '../../../../util/date.js';

import { getShippingFee } from '../../ShippingHelpers.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';

import Icon from '../../../../components/Content/Icon.js';
import FileLoader from '../../../../components/Content/FileLoader.js';
import Price from '../../../Currency/components/view/Price.js';
import CmsPanel from '../../../Cms/components/content/CmsPanel.js';

import MondialRelayProvider from '../provider/MondialRelayProvider.js';

function ShippingMethodsSelect({ intl, methods, address, items, user, isEstimation, onSelect }) {
    const [selectedMethod, handleSelectMethod] = useState(null);

    const renderProvider = method => {
        switch(method.provider) {
            case 'mondialrelay':
                return <MondialRelayProvider address={address} items={items} setAddress={address => onSelect(selectedMethod, address)} />;

            case 'colissimo':
                return null;

            default:
                return null;
        }
    };

    const needProviderSelection = method => {
        switch(method.provider) {
            case 'mondialrelay':
                return true;

            case 'colissimo':
            default:
                return false;
        }
    };

    const renderEstimatedDeliveryDate = method => {
        const { deliveryTime } = method;
        if(deliveryTime) {
            const dates = [...new Set(deliveryTime.split('-').map(delayInDay => {
				const date = dateAdd('', delayInDay, 'day');
                let suppDays = 0;
				// if(dateWeekDay(new Date()) === 0) {
				// 	suppDays += 1;
				// }
				if(dateWeekDay(new Date()) === 6) {
					suppDays += 1;
				}
                if(dateWeekDay(date) === 0) {
                    suppDays += 1;
                } else if(dateWeekDay(date) === 6) {
                    suppDays += 2;
                }
                return dateFormat(dateAdd(date, suppDays, 'day'), 'LL');
            }))];
            // console.log(dates);

            return (
                <Fragment>
                    <Icon icon="info" /> {dates.length > 1 ? <FormattedMessage id="shippingDeliveryEstimationDate2" defaultMessage="Estimated delivery date: between {date1} and {date2}" values={{ date1: dates[0], date2: dates[1] }} /> : <FormattedMessage id="shippingDeliveryEstimationDate1" defaultMessage="Estimated delivery date: {date} " values={{ date: dates[0] }} />}
                </Fragment>
            );
        }
        return null;
    };

    return (
        <div className="">
            <span className="h5"><Icon icon="cap" /> <FormattedMessage id="shippingMethodSelectTitle" defaultMessage="Select your delivery" /></span>
            <div className="p-4">
            {methods.map(method => {
                const shippingFee = getShippingFee(address, method, items, user);
                const isSelected = selectedMethod && selectedMethod._id === method._id;
                const hasFreeShippingStep = method && method.calculation && method.calculation.freeShippingStep && method.calculation.freeShippingTreshold;
                const handleClickMethod = () => { isEstimation || !needProviderSelection(method) ? onSelect(method, address) : handleSelectMethod(method); };
                return (
                    <Fragment key={method._id}>
                        <Row className="mb-3 p-2 border rounded bg-white" style={{ cursor: 'pointer' }} onClick={handleClickMethod}>
                            <Col xs="12" className="my-auto text-center">
                                {method.image && <FileLoader filename={method.image} className="w-50 w-md-25 d-inline-block" />}
                                <span className="d-block h6">{method.carrier}</span>
                                <small>{getTranslation(method, 'description', intl.locale)}</small>
                            </Col>
                            <Col className="my-auto text-center text-md-start">
                                {method.deliveryTime && (
                                    <Fragment>
                                        <FormattedMessage id="shippingDeliveryTimeInfo" defaultMessage="Delivery in {days} working days" values={{ days: method.deliveryTime }} /><br />
                                        <small>{renderEstimatedDeliveryDate(method)}</small>
                                    </Fragment>
                                )}
                            </Col>
                            <Col xs="12" md className="d-block pt-3 pt-md-0 my-auto text-center text-md-end">
                                <h4 className={classnames(shippingFee > 0 ? 'text-secondary' : 'text-success')}>{shippingFee > 0 ? <Price amount={shippingFee} /> : <span className="font-weight-bold text-uppercase"><FormattedMessage id="free" defaultMessage="Free" /></span>}</h4>
                                {hasFreeShippingStep && shippingFee > 0 && <h5 className="text-success"><FormattedMessage id="shippingFreeInfo" defaultMessage="Free shipping from {value}" values={{ value: <Price amount={hasFreeShippingStep} /> }} /></h5>}
                                {!isSelected && (
                                    <Button variant="link" className="px-0" onClick={handleClickMethod}>
                                        <FormattedMessage id="select" defaultMessage="Select" /> <Icon icon="chevron-right" />
                                    </Button>
                                )}
                            </Col>
                            {isEstimation && needProviderSelection(method) && (
                                <Col xs="12" className="text-center">
                                    <strong><FormattedMessage id="shippingDeliveryEstimationText" defaultMessage="This is an estimate of the delivery costs. The selection of the place of delivery will be made in the next step." /></strong>
                                </Col>
                            )}
                        </Row>
                        {!isEstimation && needProviderSelection(method) && (
                            <Collapse in={isSelected}>
                                <div className="mb-2 pt-1 pb-3 mx-n4 border-bottom">
                                    <CmsPanel slug={`shipping-provider-${method.provider || method.identifier}-info`} />
                                     {renderProvider(method)}
                                </div>
                            </Collapse>
                        )}
                    </Fragment>
                );
            })}
            </div>
        </div>
    );
}

ShippingMethodsSelect.defaultProps = {
    user: null,
    isEstimation: false,
};

ShippingMethodsSelect.propTypes = {
    intl: PropTypes.object.isRequired,
    methods: PropTypes.arrayOf(PropTypes.object).isRequired,
    address: PropTypes.object.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    user: PropTypes.object,
    isEstimation: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
};

export default injectIntl(ShippingMethodsSelect);
